import React, {useState, useCallback} from 'react';
import styles from './input-common.module.scss';

export interface CommonInputProps {
    error?: boolean;
    helperText?: string;
    errorText?: string;
    required?: boolean;
    disabled?: boolean;
    id?: string;
    label?: string;
    className?: string;
    labelClassName?: string;
}

export const useInputCommon = (
    props: CommonInputProps & {
        onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
        onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
        inputType?: string;
    }
) => {
    const {
        error = false,
        helperText,
        errorText,
        required = false,
        disabled = false,
        id,
        label,
        className = '',
        labelClassName = '',
        onFocus,
        onBlur,
        inputType = 'input',
    } = props;

    const [focused, setFocused] = useState(false);

    const inputId = id || `${inputType}-${Math.random().toString(36).substr(2, 9)}`;

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            if (onFocus) {
                onFocus(e);
            }
        },
        [onFocus]
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            if (onBlur) {
                onBlur(e);
            }
        },
        [onBlur]
    );

    const containerClasses = `${styles.inputContainer} ${className}`.trim();
    const labelClasses = `${styles.label} ${disabled ? styles.disabled : ''} ${labelClassName}`.trim();

    const inputAriaAttributes = {
        'aria-invalid': error,
        'aria-describedby': error && errorText ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined,
        'aria-required': required || undefined,
    };

    const errorTextElement =
        error && errorText ? (
            <div id={`${inputId}-error`} className={styles.errorText}>
                {errorText}
            </div>
        ) : null;

    const helperTextElement =
        !error && helperText ? (
            <div id={`${inputId}-helper`} className={styles.helperText}>
                {helperText}
            </div>
        ) : null;

    const labelElement = label ? (
        <label htmlFor={inputId} className={labelClasses}>
            {label}
            {required && <span className={styles.required}>*</span>}
        </label>
    ) : null;

    return {
        inputId,
        focused,
        handleFocus,
        handleBlur,
        containerClasses,
        labelClasses,
        inputAriaAttributes,
        errorTextElement,
        helperTextElement,
        labelElement,
    };
};

export const mergeClassNames = (...classNames: Array<string | undefined | false>): string => {
    return classNames.filter(Boolean).join(' ');
};
